import React, { useState, useEffect } from 'react';
import { X, Phone, Lock, Loader2, User } from 'lucide-react';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Swal from 'sweetalert2';

const OtpLoginModal = ({ isOpen, onClose, onSuccess, initialName = '', initialPhone = '' }) => {
  const { setUser, ROLES } = useAuth();
  const [step, setStep] = useState(1); // 1: Phone input, 2: OTP verification
  const [phone, setPhone] = useState(initialPhone);
  const [name, setName] = useState(initialName);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Update name and phone when initialName/initialPhone props change or modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialName && initialName.trim() !== '') {
        setName(initialName);
      }
      if (initialPhone && initialPhone.trim() !== '') {
        setPhone(initialPhone);
      }
    }
  }, [isOpen, initialName, initialPhone]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!name || name.trim().length < 2) {
      setError('Please enter your name');
      return;
    }
    if (!phone || phone.trim().length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.sendOtp(phone.trim());
      
      // OTP is sent via SMS - don't display it in the message
      Swal.fire({
        icon: 'info',
        title: 'OTP Sent',
        text: `OTP has been sent to ${phone}. Please check your SMS and enter the code.`,
        confirmButtonColor: '#2563eb'
      });

      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to send OTP. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.verifyOtp(phone.trim(), otp.trim(), name.trim());
      
      // Handle different response structures
      const loginData = response.value || response.data || response;
      
      // Map role name to role ID
      let roleId = ROLES.PASSENGER;
      const roleName = loginData.role || loginData.Role || 'Passenger';
      if (roleName === 'Manager' || roleName === 'manager') {
        roleId = ROLES.MANAGER;
      } else if (roleName === 'Conductor' || roleName === 'conductor') {
        roleId = ROLES.CONDUCTOR;
      } else if (roleName === 'Passenger' || roleName === 'passenger') {
        roleId = ROLES.PASSENGER;
      }
      
      // Create user data object
      const userData = {
        id: loginData.userID || loginData.UserID || 0,
        username: loginData.username || loginData.Username || '',
        fullName: loginData.fullName || loginData.FullName || '',
        role: roleId,
        roleName: roleName,
        permissions: loginData.permissions || loginData.Permissions || []
      };
      
      // Store token and user info
      const token = loginData.token || loginData.Token || '';
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update AuthContext state to show user as logged in
      setUser(userData);

      // Show success message and wait for it to close before calling onSuccess
      await Swal.fire({
        icon: 'success',
        title: 'Login Successful',
        text: `Welcome, ${userData.fullName}!`,
        confirmButtonColor: '#2563eb',
        timer: 1500
      });

      // Reset form
      setStep(1);
      setPhone('');
      setName('');
      setOtp('');
      onClose();

      // Call onSuccess after alert is dismissed to trigger scroll
      if (onSuccess) {
        // Small delay to ensure modal is closed and DOM is updated
        setTimeout(() => {
          onSuccess(userData);
        }, 100);
      }
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
      Swal.fire({
        icon: 'error',
        title: 'Invalid OTP',
        text: err.message || 'Invalid OTP. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {step === 1 ? 'Login / Register' : 'Verify OTP'}
          </h2>
          <p className="text-gray-600 text-sm">
            {step === 1 
              ? 'Enter your phone number to receive OTP' 
              : `Enter the OTP sent to ${phone}`}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your name"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="07XXXXXXXX"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Sending OTP...
                </>
              ) : (
                'Send OTP'
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleBack}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-400 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OtpLoginModal;

